// src/components/AuthBridge.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../firebase';
import { Loader2, AlertCircle } from 'lucide-react';
import Logo from './Logo';
import Footer from './Footer';
import SEO from './SEO';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

function AuthBridge() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const code = searchParams.get('code');
  const redirectParam = searchParams.get('redirect');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Store timeout IDs for cleanup
    let redirectTimeout = null;
    
    // Clean URL helper function
    const cleanUrl = () => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('code');
      newSearchParams.delete('redirect');
      
      const newSearch = newSearchParams.toString();
      const newUrl = newSearch 
        ? `${window.location.pathname}?${newSearch}`
        : window.location.pathname;
      
      window.history.replaceState({}, '', newUrl);
    };

    // Validate redirect path to prevent open redirects
    const sanitizeRedirect = (path) => {
      if (!path) return '/';
      
      // Must start with / and not be an external URL
      if (path.startsWith('/') && !path.startsWith('//') && !path.includes('://')) {
        return path;
      }
      
      // Default to home if invalid
      return '/';
    };

    // If no code, redirect to auth page
    if (!code) {
      console.error('[AuthBridge] No code provided in URL');
      setError('Missing authentication code. Please request a new sign-in link.');
      setLoading(false);
      // Use a shorter delay in test/development environments to speed up tests
      // In CI, use 100ms; in localhost, use 1 second; in production, use 3 seconds
      const redirectDelay = process.env.REACT_APP_CI === 'true' ? 100 : 
                          (window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1') ? 1000 : 3000;
      console.log('[AuthBridge] No code - scheduling redirect in', redirectDelay, 'ms');
      redirectTimeout = setTimeout(() => {
        console.log('Redirecting to /auth due to error...');
        navigate('/auth', { replace: true });
      }, redirectDelay);
      
      // Cleanup timeout on unmount
      return () => {
        if (redirectTimeout) {
          clearTimeout(redirectTimeout);
        }
      };
    }

    const handleExchange = async () => {
      try {
        console.log('[AuthBridge] Starting code exchange');
        
        // Determine API URL
        const hostname = window.location.hostname;
        const port = window.location.port;
        const isLocalNetwork = /^(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/.test(hostname);
        const isDevelopment = hostname === 'localhost' || 
                              hostname === '127.0.0.1' || 
                              isLocalNetwork ||
                              port === '3000' ||
                              process.env.NODE_ENV === 'development';
        
        // Support both kebab-case and camelCase endpoint names
        const endpointPath = '/api/exchange-auth-bridge-code'; // or '/api/exchangeAuthBridgeCode'
        const apiUrl = API_BASE_URL
          ? `${API_BASE_URL}${endpointPath}`
          : isDevelopment
            ? `http://127.0.0.1:4000${endpointPath}`
            : `https://us-central1-epiphoric-prod.cloudfunctions.net/api${endpointPath}`;

        console.log('[AuthBridge] Calling backend:', apiUrl);

        // Call backend to exchange code for custom token
        console.log('[AuthBridge] Request details:', {
          url: apiUrl,
          code: code ? `${code.substring(0, 3)}...` : 'MISSING',
          codeLength: code ? code.length : 0
        });
        
        // Add timeout to prevent hanging (10 seconds)
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);
        
        let response;
        try {
          response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ code }),
            signal: controller.signal,
          });
        } catch (fetchError) {
          clearTimeout(timeoutId);
          if (fetchError.name === 'AbortError') {
            throw new Error('Request timeout - authentication service not responding');
          }
          throw fetchError;
        }
        clearTimeout(timeoutId);

        console.log('[AuthBridge] Response received:', {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
          contentType: response.headers.get('content-type')
        });

        // Check if response is JSON before parsing
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const text = await response.text();
          console.error('[AuthBridge] Non-JSON response:', text.substring(0, 200));
          throw new Error(`Server returned ${response.status}: ${text.substring(0, 100)}`);
        }

        const data = await response.json();
        console.log('[AuthBridge] Response data:', {
          hasCustomToken: !!data.customToken,
          hasError: !!data.error,
          errorCode: data.code
        });

        if (!response.ok) {
          const errorMessage = data.error || 'Failed to exchange code';
          const errorCode = data.code || 'UNKNOWN_ERROR';
          console.error('[AuthBridge] Exchange failed:', {
            status: response.status,
            error: errorMessage,
            code: errorCode,
            fullResponse: data
          });
          throw new Error(errorMessage);
        }

        if (!data.customToken) {
          console.error('[AuthBridge] No custom token in response:', data);
          throw new Error('No custom token received from server');
        }

        console.log('[AuthBridge] Custom token received, signing in user');

        // Sign in with the custom token
        const userCredential = await signInWithCustomToken(auth, data.customToken);
        console.log('[AuthBridge] User signed in successfully:', userCredential.user.uid);

        // Verify user is set (should be immediate)
        if (!auth.currentUser) {
          throw new Error('User not set after sign-in');
        }

        // Wait briefly for auth state to be persisted to sessionStorage
        // This is especially important on mobile/storage-partitioned browsers
        // to prevent "missing initial state" errors
        await new Promise(resolve => setTimeout(resolve, 200));
        
        console.log('[AuthBridge] Auth state persisted, redirecting');
        
        // Clean up URL (remove query params)
        cleanUrl();
        
        // Redirect to target path (sanitized) - auth state is now persisted
        const redirectPath = sanitizeRedirect(redirectParam);
        navigate(redirectPath, { replace: true });
        
      } catch (err) {
        console.error('[AuthBridge] Authentication error:', {
          message: err.message,
          name: err.name,
          stack: err.stack,
          url: window.location.href,
          code: code ? `${code.substring(0, 3)}...` : 'MISSING'
        });
        
        // Provide user-friendly error message
        let errorMessage = 'Failed to sign in automatically. Please sign in manually.';
        if (err.message.includes('404')) {
          errorMessage = 'Authentication service not available. Please try again later.';
        } else if (err.message.includes('410') || err.message.includes('expired') || err.message.includes('already used')) {
          errorMessage = 'This sign-in link has expired or been used. Please request a new one from the mobile app.';
        } else if (err.message.includes('401') || err.message.includes('Invalid')) {
          errorMessage = 'Invalid authentication code. Please request a new sign-in link.';
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        setError(errorMessage);
        
        // Clean up URL even on error (for security)
        cleanUrl();
        
        // Redirect to auth page after delay
        // Use a shorter delay in test/development environments to speed up tests
        // In CI, use 100ms; in localhost, use 1 second; in production, use 3 seconds
        const redirectDelay = process.env.REACT_APP_CI === 'true' ? 100 : 
                            (window.location.hostname === 'localhost' || 
                             window.location.hostname === '127.0.0.1') ? 1000 : 3000;
        console.log('[AuthBridge] Scheduling redirect in', redirectDelay, 'ms');
        redirectTimeout = setTimeout(() => {
          console.log('Redirecting to /auth due to error...');
          navigate('/auth', { replace: true, state: { error: errorMessage } });
        }, redirectDelay);
      } finally {
        setLoading(false);
      }
    };

    handleExchange();
    
    // Cleanup function for the entire effect
    return () => {
      // Clear any pending redirect timeouts on unmount
      if (redirectTimeout) {
        clearTimeout(redirectTimeout);
      }
    };
  }, [code, redirectParam, searchParams, navigate]);

  return (
    <>
      <SEO 
        title="Signing In - Epiphoric"
        description="Securely logging you in to Epiphoric"
        url="/auth-bridge"
      />
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Logo />
          </div>
          
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 shadow-2xl border border-white/20">
            {loading && !error && (
              <div className="text-center">
                <Loader2 className="w-12 h-12 animate-spin text-white mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-white mb-2">Securely logging you in...</h2>
                <p className="text-white/70">Please wait while we verify your authentication code.</p>
              </div>
            )}
            
            {error && (
              <div className="text-center">
                <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-white mb-2">Authentication Failed</h2>
                <p className="text-white/70 mb-4">{error}</p>
                <p className="text-white/50 text-sm">Redirecting to login page...</p>
              </div>
            )}
          </div>
        </div>
        
        <Footer variant="ghost" />
      </div>
    </>
  );
}

export default AuthBridge;

